import { useState } from 'react';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  location: string;
  gradeLevel: string;
  type: 'online' | 'classroom' | 'face-to-face';
  rating: number;
  quote: string;
  transformation: string;
  avatar: string;
  nationality: string;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "Aaliyah Washington",
    role: "Student",
    location: "Brooklyn, NY",
    gradeLevel: "Grade 8",
    type: "face-to-face",
    rating: 5,
    quote: "I failed every single math test in 7th grade. My mom was in tears at parent-teacher conferences. Mr. Augustine sat with me for the first session and within an hour I understood integers better than I ever had. He doesn't just teach — he makes you feel like you were born to do math. I finished 8th grade with a 94 average.",
    transformation: "Failed every test → 94 average",
    avatar: "https://public.readdy.ai/ai/img_res/ea614e4d0a8677eaccb16ac86af1713a.jpg",
    nationality: "African American"
  },
  {
    id: 2,
    name: "Carlos Mendoza",
    role: "Student",
    location: "Bronx, NY",
    gradeLevel: "Grade 11 — Algebra 2",
    type: "online",
    rating: 5,
    quote: "I was about to drop Algebra 2 and take a lower-level class. My counselor said it might be better for me. But my dad found Mr. Augustine online. After just four online sessions, I not only stayed in Algebra 2 — I started tutoring my classmates. His short methods are like cheat codes for math. I scored 88 on my Regents exam.",
    transformation: "About to drop the class → 88 on Regents",
    avatar: "https://readdy.ai/api/search-image?query=confident%20young%20Latino%20teenage%20boy%20smiling%20natural%20portrait%20school%20setting%20bright%20background&width=100&height=100&seq=t2&orientation=squarish",
    nationality: "Latino"
  },
  {
    id: 3,
    name: "Jennifer Park",
    role: "Parent",
    location: "Flushing, Queens, NY",
    gradeLevel: "Grade 5 (daughter)",
    type: "face-to-face",
    rating: 5,
    quote: "My daughter Mia was diagnosed with math anxiety by her school counselor. She would cry before every math test. We tried three other tutors — nothing worked. Mr. Augustine came to our home twice a week. He was so patient, so encouraging. Within two months, Mia was raising her hand in class. She got a 4 on her state test. I still can't believe it.",
    transformation: "Math anxiety & tears → State test score of 4",
    avatar: "https://readdy.ai/api/search-image?query=Korean%20American%20mother%20smiling%20warmly%20natural%20portrait%20professional%20setting%20bright%20background&width=100&height=100&seq=t3&orientation=squarish",
    nationality: "Korean American"
  },
  {
    id: 4,
    name: "Darius Thompson",
    role: "Student",
    location: "Harlem, NY",
    gradeLevel: "Grade 12 — Pre-Calculus",
    type: "online",
    rating: 5,
    quote: "I needed Pre-Calc to get into the engineering program at City College. I had a 58 average going into the second semester. Mr. Augustine worked with me online every Saturday morning. He explained limits and derivatives in ways my teacher never could. I ended the year with an 82 and got accepted into the program. He literally changed my life's direction.",
    transformation: "58 average → 82 average, college acceptance",
    avatar: "https://readdy.ai/api/search-image?query=confident%20young%20African%20American%20teenage%20boy%20smiling%20natural%20portrait%20school%20setting%20bright%20background&width=100&height=100&seq=t4&orientation=squarish",
    nationality: "African American"
  },
  {
    id: 5,
    name: "Priya Sharma",
    role: "Parent",
    location: "Edison, NJ",
    gradeLevel: "Grade 7 (son)",
    type: "online",
    rating: 5,
    quote: "My son Arjun is very bright but math was his weak spot. He would spend three hours on homework and still get it wrong. Mr. Augustine's online sessions were a revelation. He teaches with such clarity and humor — Arjun actually laughs during math lessons now! His grade went from a C- to an A in one semester. We are so grateful.",
    transformation: "C- to A in one semester",
    avatar: "https://readdy.ai/api/search-image?query=Indian%20American%20mother%20smiling%20warmly%20natural%20portrait%20professional%20setting%20bright%20background&width=100&height=100&seq=t5&orientation=squarish",
    nationality: "Indian American"
  },
  {
    id: 6,
    name: "Marcus O'Brien",
    role: "Student",
    location: "Staten Island, NY",
    gradeLevel: "SAT Prep",
    type: "face-to-face",
    rating: 5,
    quote: "I took the SAT twice and scored 480 in math both times. My dream school required a 600 minimum. My parents hired Mr. Augustine for face-to-face sessions three times a week. He broke down every single question type. On my third attempt, I scored 650. I got into my dream school. I owe that man everything.",
    transformation: "480 SAT (twice) → 650 SAT",
    avatar: "https://readdy.ai/api/search-image?query=confident%20Irish%20American%20teenage%20boy%20smiling%20natural%20portrait%20school%20setting%20bright%20background&width=100&height=100&seq=t6&orientation=squarish",
    nationality: "Irish American"
  },
  {
    id: 7,
    name: "Fatima Al-Hassan",
    role: "Student",
    location: "Jersey City, NJ",
    gradeLevel: "Grade 10 — Geometry",
    type: "online",
    rating: 5,
    quote: "Geometry was impossible for me. I couldn't visualize anything — proofs, theorems, all of it was a blur. Mr. Augustine taught me online and used the most brilliant visual explanations. He has a way of connecting every concept to something real. I went from a 55 to a 91 in Geometry. My teacher asked me what happened over the summer!",
    transformation: "55 → 91 in Geometry",
    avatar: "https://readdy.ai/api/search-image?query=young%20Arab%20American%20teenage%20girl%20smiling%20warmly%20natural%20portrait%20school%20setting%20bright%20background&width=100&height=100&seq=t7&orientation=squarish",
    nationality: "Arab American"
  },
  {
    id: 8,
    name: "Robert Kowalski",
    role: "Parent",
    location: "Greenpoint, Brooklyn, NY",
    gradeLevel: "Grade 3 (daughter)",
    type: "face-to-face",
    rating: 5,
    quote: "My daughter Sofia was held back in 2nd grade partly because of math. We were terrified she'd fall further behind. Mr. Augustine worked with her face-to-face and I watched the sessions — his patience is extraordinary. He never once made her feel stupid. By the end of Grade 3, she passed her state test with a Level 3. We cried happy tears.",
    transformation: "Held back in 2nd grade → Passed state test Level 3",
    avatar: "https://readdy.ai/api/search-image?query=Polish%20American%20father%20smiling%20warmly%20natural%20portrait%20professional%20setting%20bright%20background&width=100&height=100&seq=t8&orientation=squarish",
    nationality: "Polish American"
  },
  {
    id: 9,
    name: "Keisha Brown",
    role: "Adult Learner",
    location: "Newark, NJ",
    gradeLevel: "GED Prep",
    type: "online",
    rating: 5,
    quote: "I dropped out of high school at 16 and spent 20 years telling myself I was just bad at math. At 36, I decided to get my GED. The math section terrified me. Mr. Augustine tutored me online and treated me with so much respect and dignity. He said 'You're not bad at math — you just never had the right teacher.' I passed my GED math section on the first try.",
    transformation: "20 years of math fear → GED passed on first try",
    avatar: "https://readdy.ai/api/search-image?query=confident%20African%20American%20woman%20in%20her%2030s%20smiling%20warmly%20natural%20portrait%20professional%20setting%20bright%20background&width=100&height=100&seq=t9&orientation=squarish",
    nationality: "African American"
  },
  {
    id: 10,
    name: "Liam Fitzgerald",
    role: "Student",
    location: "Woodside, Queens, NY",
    gradeLevel: "Grade 9 — Algebra 1",
    type: "classroom",
    rating: 5,
    quote: "Mr. Augustine taught our after-school program at my school. I was one of 12 students who were failing Algebra 1. He came in three days a week and transformed the whole group. His energy in the classroom is electric — you can't fall asleep, you can't zone out. By June, 10 out of 12 of us passed the Regents. I got an 81.",
    transformation: "Failing Algebra 1 → 81 on Regents",
    avatar: "https://readdy.ai/api/search-image?query=confident%20Irish%20American%20teenage%20boy%20smiling%20natural%20portrait%20school%20setting%20bright%20background&width=100&height=100&seq=t10&orientation=squarish",
    nationality: "Irish American"
  },
  {
    id: 11,
    name: "Yuki Tanaka",
    role: "Parent",
    location: "Fort Lee, NJ",
    gradeLevel: "Grade 6 (son)",
    type: "online",
    rating: 5,
    quote: "My son Kenji moved from Japan and struggled with the American math curriculum — especially word problems and fractions. Mr. Augustine's online lessons were perfect for us. He was so understanding of Kenji's background and built his confidence step by step. Kenji now loves math and wants to be an engineer. His teacher calls him one of the best in the class.",
    transformation: "Struggling with new curriculum → Top of the class",
    avatar: "https://readdy.ai/api/search-image?query=Japanese%20American%20mother%20smiling%20warmly%20natural%20portrait%20professional%20setting%20bright%20background&width=100&height=100&seq=t11&orientation=squarish",
    nationality: "Japanese American"
  },
  {
    id: 12,
    name: "Destiny Rivera",
    role: "Student",
    location: "South Bronx, NY",
    gradeLevel: "Grade 8 — SHSAT Prep",
    type: "face-to-face",
    rating: 5,
    quote: "I wanted to get into Stuyvesant High School but my math score on practice tests was nowhere near good enough. Mr. Augustine prepared me face-to-face for the SHSAT. He knew exactly which topics to focus on and his shortcuts saved me so much time on the test. I got into Bronx Science. My whole family celebrated for a week.",
    transformation: "Below SHSAT threshold → Accepted to Bronx Science",
    avatar: "https://readdy.ai/api/search-image?query=confident%20young%20Latina%20teenage%20girl%20smiling%20warmly%20natural%20portrait%20school%20setting%20bright%20background&width=100&height=100&seq=t12&orientation=squarish",
    nationality: "Puerto Rican American"
  },
  {
    id: 13,
    name: "Emmanuel Osei",
    role: "Student",
    location: "Flatbush, Brooklyn, NY",
    gradeLevel: "Grade 11 — Algebra 2 & Trig",
    type: "face-to-face",
    rating: 5,
    quote: "My parents are from Ghana and education means everything to our family. I was struggling badly with Algebra 2 and Trigonometry and felt like I was letting everyone down. Mr. Augustine — who I later found out is also from Ghana — worked with me face-to-face. He understood my pressure and channeled it into motivation. I scored 94 on my Regents. My father wept.",
    transformation: "Struggling & overwhelmed → 94 on Regents",
    avatar: "https://readdy.ai/api/search-image?query=confident%20young%20Ghanaian%20American%20teenage%20boy%20smiling%20natural%20portrait%20school%20setting%20bright%20background&width=100&height=100&seq=t13&orientation=squarish",
    nationality: "Ghanaian American"
  },
  {
    id: 14,
    name: "Sandra Kowalczyk",
    role: "Parent",
    location: "Ridgewood, Queens, NY",
    gradeLevel: "Grade 4 (twin boys)",
    type: "face-to-face",
    rating: 5,
    quote: "I have twin boys — one is strong in math, one is not. It was creating a real divide between them. Mr. Augustine worked with both of them together and somehow managed to challenge the stronger one while lifting up the weaker one simultaneously. By the end of the year, both boys were at grade level and their relationship improved too. He's a miracle worker.",
    transformation: "One failing, one thriving → Both at grade level",
    avatar: "https://readdy.ai/api/search-image?query=Polish%20American%20mother%20smiling%20warmly%20natural%20portrait%20professional%20setting%20bright%20background&width=100&height=100&seq=t14&orientation=squarish",
    nationality: "Polish American"
  },
  {
    id: 15,
    name: "Tyrone Jackson",
    role: "Student",
    location: "East New York, Brooklyn, NY",
    gradeLevel: "GRE Prep",
    type: "online",
    rating: 5,
    quote: "I hadn't done math in 8 years when I decided to apply to grad school. The GRE math section felt like a foreign language. Mr. Augustine's online sessions were incredibly structured — he rebuilt my foundation from scratch and then pushed me to advanced problem-solving. I scored in the 88th percentile on the GRE quant. My advisor couldn't believe my score.",
    transformation: "8 years away from math → 88th percentile GRE",
    avatar: "https://readdy.ai/api/search-image?query=confident%20African%20American%20man%20in%20his%20late%2020s%20smiling%20natural%20portrait%20professional%20setting%20bright%20background&width=100&height=100&seq=t15&orientation=squarish",
    nationality: "African American"
  },
  {
    id: 16,
    name: "Mei-Ling Chen",
    role: "Parent",
    location: "Sunset Park, Brooklyn, NY",
    gradeLevel: "Grade 2 (daughter)",
    type: "online",
    rating: 5,
    quote: "My daughter Amy was behind in 2nd grade math and her teacher suggested she might need special services. I was not ready to accept that. I found Mr. Augustine and we did online sessions twice a week. He made learning so fun — Amy would ask me when her next 'math game' was. She finished the year above grade level. No special services needed.",
    transformation: "Suggested for special services → Above grade level",
    avatar: "https://readdy.ai/api/search-image?query=Chinese%20American%20mother%20smiling%20warmly%20natural%20portrait%20professional%20setting%20bright%20background&width=100&height=100&seq=t16&orientation=squarish",
    nationality: "Chinese American"
  },
  {
    id: 17,
    name: "Brendan O'Sullivan",
    role: "Student",
    location: "Maspeth, Queens, NY",
    gradeLevel: "Grade 12 — AP Calculus",
    type: "classroom",
    rating: 5,
    quote: "Mr. Augustine ran a Saturday AP Calculus prep class at our community center. I was getting 2s on practice exams and was ready to give up on the AP exam entirely. His classroom energy is unlike anything I've experienced — he makes calculus feel like a story, not a formula. I scored a 4 on the AP exam and got college credit. Saved my parents thousands.",
    transformation: "Scoring 2s → AP score of 4, college credit earned",
    avatar: "https://readdy.ai/api/search-image?query=confident%20Irish%20American%20teenage%20boy%20smiling%20natural%20portrait%20school%20setting%20bright%20background&width=100&height=100&seq=t17&orientation=squarish",
    nationality: "Irish American"
  },
  {
    id: 18,
    name: "Amara Diallo",
    role: "Student",
    location: "Harlem, NY",
    gradeLevel: "Grade 10 — Geometry",
    type: "online",
    rating: 5,
    quote: "I came to the US from Senegal in 9th grade and the math here was taught so differently. I was lost in Geometry — the proofs, the theorems, the way they write everything. Mr. Augustine tutored me online and was so patient with my questions. He never made me feel embarrassed for not knowing. I finished Geometry with an 87 and my confidence in school completely changed.",
    transformation: "Lost in new curriculum → 87 in Geometry",
    avatar: "https://readdy.ai/api/search-image?query=confident%20young%20Senegalese%20American%20teenage%20girl%20smiling%20warmly%20natural%20portrait%20school%20setting%20bright%20background&width=100&height=100&seq=t18&orientation=squarish",
    nationality: "Senegalese American"
  },
  {
    id: 19,
    name: "Victor Hernandez",
    role: "Parent",
    location: "Washington Heights, NY",
    gradeLevel: "Grade 9 (son)",
    type: "face-to-face",
    rating: 5,
    quote: "My son Miguel was on the verge of failing 9th grade and being held back. His school called us in for a meeting. We started face-to-face sessions with Mr. Augustine immediately. What amazed me was how quickly Miguel's attitude changed — he stopped saying 'I hate math' and started saying 'I get it now.' He passed 9th grade with a 75 and is now in 10th grade on track.",
    transformation: "Verge of being held back → Passed, on track in 10th grade",
    avatar: "https://readdy.ai/api/search-image?query=Dominican%20American%20father%20smiling%20warmly%20natural%20portrait%20professional%20setting%20bright%20background&width=100&height=100&seq=t19&orientation=squarish",
    nationality: "Dominican American"
  },
  {
    id: 20,
    name: "Natasha Petrov",
    role: "Adult Learner",
    location: "Brighton Beach, Brooklyn, NY",
    gradeLevel: "HESI Nursing Exam",
    type: "online",
    rating: 5,
    quote: "I immigrated from Russia and always struggled with American-style math word problems. I needed to pass the HESI exam to get into nursing school and failed the math section twice. Mr. Augustine worked with me online and focused specifically on the types of problems that appear on the HESI. On my third attempt, I passed with a 90% in math. I start nursing school in the fall.",
    transformation: "Failed HESI math twice → 90% on third attempt",
    avatar: "https://readdy.ai/api/search-image?query=Russian%20American%20woman%20in%20her%2030s%20smiling%20warmly%20natural%20portrait%20professional%20setting%20bright%20background&width=100&height=100&seq=t20&orientation=squarish",
    nationality: "Russian American"
  },
  {
    id: 21,
    name: "Isaiah Freeman",
    role: "Student",
    location: "Jamaica, Queens, NY",
    gradeLevel: "Grade 7",
    type: "classroom",
    rating: 5,
    quote: "I was in Mr. Augustine's after-school classroom program at my middle school. I used to sit in the back and pretend to understand. He noticed me on day two and called me up to the board — I was terrified. But he guided me through the problem so gently that I got it right. After that, I always sat in the front. I ended 7th grade with an 88 in math.",
    transformation: "Hiding in the back, pretending → Front row, 88 average",
    avatar: "https://readdy.ai/api/search-image?query=confident%20young%20African%20American%20teenage%20boy%20smiling%20natural%20portrait%20school%20setting%20bright%20background&width=100&height=100&seq=t21&orientation=squarish",
    nationality: "African American"
  },
  {
    id: 22,
    name: "Grace Kim",
    role: "Parent",
    location: "Bayside, Queens, NY",
    gradeLevel: "Grade 11 (daughter)",
    type: "online",
    rating: 5,
    quote: "My daughter Hannah is a straight-A student in every subject except math. It was her one weakness and it was affecting her GPA and her confidence. We tried online tutoring with two other services — no improvement. Mr. Augustine was different from the first session. He identified exactly where her gaps were and filled them systematically. Hannah got a 96 on her Algebra 2 Regents. We were speechless.",
    transformation: "Only weakness, affecting GPA → 96 on Regents",
    avatar: "https://readdy.ai/api/search-image?query=Korean%20American%20mother%20smiling%20warmly%20natural%20portrait%20professional%20setting%20bright%20background&width=100&height=100&seq=t22&orientation=squarish",
    nationality: "Korean American"
  },
  {
    id: 23,
    name: "Jamal Okafor",
    role: "Student",
    location: "Crown Heights, Brooklyn, NY",
    gradeLevel: "Grade 5",
    type: "face-to-face",
    rating: 5,
    quote: "I used to think I was dumb because everyone else in my class seemed to get math and I didn't. Mr. Augustine came to my house and the first thing he told me was 'You are not dumb — math just hasn't been explained to you the right way yet.' He was right. Within weeks I was getting 90s on tests. I won the math award at my school's end-of-year ceremony.",
    transformation: "Felt 'dumb' → Won school math award",
    avatar: "https://readdy.ai/api/search-image?query=confident%20young%20Nigerian%20American%20boy%20smiling%20warmly%20natural%20portrait%20school%20setting%20bright%20background&width=100&height=100&seq=t23&orientation=squarish",
    nationality: "Nigerian American"
  },
  {
    id: 24,
    name: "Rosa Gutierrez",
    role: "Parent",
    location: "Bushwick, Brooklyn, NY",
    gradeLevel: "Kindergarten & Grade 3 (two children)",
    type: "face-to-face",
    rating: 5,
    quote: "I have two children — one in kindergarten and one in 3rd grade. Both were struggling. Mr. Augustine worked with them both in the same session, adjusting his teaching style for each child's age and level. I watched in amazement. My kindergartner now counts and adds confidently, and my 3rd grader passed her state test. He has a gift that is truly rare.",
    transformation: "Both children struggling → Both passing, one state test success",
    avatar: "https://readdy.ai/api/search-image?query=Mexican%20American%20mother%20smiling%20warmly%20natural%20portrait%20professional%20setting%20bright%20background&width=100&height=100&seq=t24&orientation=squarish",
    nationality: "Mexican American"
  },
  {
    id: 25,
    name: "Daniel Nguyen",
    role: "Student",
    location: "Elmhurst, Queens, NY",
    gradeLevel: "Grade 12 — SAT & Pre-Calculus",
    type: "online",
    rating: 5,
    quote: "I needed both a strong SAT score and a good Pre-Calc grade to get into my top-choice university. I was struggling with both. Mr. Augustine's online sessions covered both simultaneously — he showed me how the concepts overlap. My SAT math went from 520 to 680 and my Pre-Calc average went from 71 to 89. I got into NYU's engineering program. This man is a legend.",
    transformation: "520 SAT & 71 Pre-Calc → 680 SAT & 89 Pre-Calc, NYU accepted",
    avatar: "https://readdy.ai/api/search-image?query=confident%20young%20Vietnamese%20American%20teenage%20boy%20smiling%20natural%20portrait%20school%20setting%20bright%20background&width=100&height=100&seq=t25&orientation=squarish",
    nationality: "Vietnamese American"
  }
];

const typeColors: Record<string, string> = {
  online: 'bg-teal-100 text-teal-700',
  classroom: 'bg-amber-100 text-amber-700',
  'face-to-face': 'bg-orange-100 text-orange-700',
};

const typeLabels: Record<string, string> = {
  online: 'Online Tutoring',
  classroom: 'Classroom Teaching',
  'face-to-face': 'Face-to-Face Tutoring',
};

const typeIcons: Record<string, string> = {
  online: 'ri-computer-line',
  classroom: 'ri-school-line',
  'face-to-face': 'ri-user-heart-line',
};

export default function Testimonials() {
  const [filter, setFilter] = useState<'all' | 'online' | 'classroom' | 'face-to-face'>('all');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [visibleCount, setVisibleCount] = useState(9);

  const filtered = filter === 'all' ? testimonials : testimonials.filter(t => t.type === filter);
  const visible = filtered.slice(0, visibleCount);

  return (
    <section className="py-24 bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-bold mb-5">
            <i className="ri-star-fill text-orange-500"></i>
            REAL STUDENTS. REAL RESULTS.
          </div>
          <h2 className="text-5xl font-bold text-gray-900 mb-4">
            25 of the Thousands of Lives Transformed by<br />
            <span className="text-orange-600">&ldquo;Mr. Augustine&rdquo; (Anyimadu-Ahenkae)</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            From kindergartners who feared numbers to adults conquering the GRE — these are the real stories of students and families whose mathematical journeys were forever changed.
          </p>

          {/* Stats Bar */}
          <div className="flex flex-wrap items-center justify-center gap-8 mt-10 mb-12">
            {[
              { icon: 'ri-user-smile-line', value: '5,000+', label: 'Students Helped' },
              { icon: 'ri-star-fill', value: '4.9/5', label: 'Average Rating' },
              { icon: 'ri-trophy-line', value: '30+', label: 'Years Teaching' },
              { icon: 'ri-global-line', value: '40+', label: 'Nationalities Served' },
            ].map((stat, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-xl">
                  <i className={`${stat.icon} text-2xl text-orange-600`}></i>
                </div>
                <div className="text-left">
                  <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Filter Tabs */}
          <div className="inline-flex items-center gap-2 bg-gray-100 rounded-full p-1">
            {(['all', 'online', 'classroom', 'face-to-face'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => { setFilter(tab); setVisibleCount(9); }}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  filter === tab
                    ? 'bg-orange-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab === 'all' ? 'All Stories' : typeLabels[tab]}
              </button>
            ))}
          </div>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visible.map((t) => {
            const isExpanded = expanded === t.id;
            return (
              <div
                key={t.id}
                className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col overflow-hidden group"
              >
                {/* Card Top */}
                <div className="p-6 flex-1">
                  {/* Type Badge */}
                  <div className="flex items-center justify-between mb-4">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${typeColors[t.type]}`}>
                      <i className={`${typeIcons[t.type]} text-sm`}></i>
                      {typeLabels[t.type]}
                    </span>
                    <div className="flex items-center gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-sm text-orange-400"></i>
                      ))}
                    </div>
                  </div>

                  {/* Quote */}
                  <div className="relative mb-4">
                    <i className="ri-double-quotes-l text-4xl text-orange-100 absolute -top-2 -left-1"></i>
                    <p className={`text-gray-700 text-sm leading-relaxed relative z-10 pl-4 ${!isExpanded ? 'line-clamp-4' : ''}`}>
                      {t.quote}
                    </p>
                  </div>

                  {t.quote.length > 200 && (
                    <button
                      onClick={() => setExpanded(isExpanded ? null : t.id)}
                      className="text-orange-600 text-xs font-semibold hover:text-orange-700 transition-colors cursor-pointer mb-3"
                    >
                      {isExpanded ? 'Show less ↑' : 'Read full story ↓'}
                    </button>
                  )}

                  {/* Transformation Badge */}
                  <div className="flex items-start gap-2 bg-green-50 border border-green-100 rounded-xl px-3 py-2 mt-2">
                    <i className="ri-arrow-right-up-line text-green-600 text-sm mt-0.5 flex-shrink-0"></i>
                    <span className="text-green-700 text-xs font-semibold leading-snug">{t.transformation}</span>
                  </div>
                </div>

                {/* Card Bottom — Author */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center gap-3">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-12 h-12 rounded-full object-cover object-top flex-shrink-0 border-2 border-orange-200"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-gray-900 text-sm truncate">{t.name}</div>
                    <div className="text-xs text-gray-500 truncate">{t.gradeLevel}</div>
                    <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                      <i className="ri-map-pin-line text-orange-400"></i>
                      <span className="truncate">{t.location}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Load More */}
        {visibleCount < filtered.length && (
          <div className="text-center mt-10">
            <button
              onClick={() => setVisibleCount(prev => Math.min(prev + 6, filtered.length))}
              className="px-8 py-3 border-2 border-orange-600 text-orange-600 rounded-xl font-semibold hover:bg-orange-600 hover:text-white transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
            >
              Load More Stories <i className="ri-arrow-down-line"></i>
            </button>
            <p className="text-sm text-gray-400 mt-2">
              Showing {visibleCount} of {filtered.length} stories
            </p>
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-10 text-center text-white relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/4 w-80 h-80 rounded-full opacity-10"
              style={{ background: 'radial-gradient(circle, #FF6B35 0%, transparent 70%)' }}></div>
            <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full opacity-10"
              style={{ background: 'radial-gradient(circle, #FF6B35 0%, transparent 70%)' }}></div>
          </div>
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600/20 border border-orange-500/40 rounded-full text-orange-300 text-sm font-bold mb-6">
              <i className="ri-trophy-line"></i>
              YOUR TRANSFORMATION STARTS NOW
            </div>
            <div className="text-5xl mb-4">🏆</div>
            <h3 className="text-3xl font-bold mb-3">Ready to Write Your Own Success Story?</h3>
            <p className="text-gray-300 text-lg mb-4 max-w-2xl mx-auto">
              Every student above started exactly where you are. The only difference? They chose to act. Pick a membership plan today and get instant access to the materials that <strong className="text-orange-400">guarantee your success</strong>.
            </p>

            {/* Mini plan highlights */}
            <div className="grid md:grid-cols-3 gap-4 max-w-3xl mx-auto mb-8 mt-6">
              {[
                { plan: 'Free Trial', price: '$0', duration: '14 days', highlight: 'Try before you commit', icon: 'ri-gift-line', color: 'border-gray-500 bg-white/5' },
                { plan: 'Standard', price: '$29.99', duration: '/month', highlight: 'Most popular — all grades + test prep', icon: 'ri-star-fill', color: 'border-orange-500 bg-orange-600/15', badge: 'MOST POPULAR' },
                { plan: 'All Access', price: '$49.99', duration: '/month', highlight: '1-on-1 with Mr. Augustine included', icon: 'ri-vip-crown-line', color: 'border-amber-400 bg-amber-500/10' },
              ].map((p, i) => (
                <div key={i} className={`relative border-2 rounded-xl p-4 text-left ${p.color}`}>
                  {p.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-600 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                      {p.badge}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 flex items-center justify-center">
                      <i className={`${p.icon} text-orange-400 text-lg`}></i>
                    </div>
                    <span className="font-bold text-white text-sm">{p.plan}</span>
                  </div>
                  <div className="text-2xl font-bold text-white mb-1">{p.price}<span className="text-sm font-normal text-gray-400">{p.duration}</span></div>
                  <p className="text-gray-400 text-xs leading-snug">{p.highlight}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href="#pricing"
                className="px-10 py-4 bg-orange-600 text-white rounded-xl font-bold hover:bg-orange-500 transition-all hover:scale-105 whitespace-nowrap inline-flex items-center gap-2 shadow-xl shadow-orange-900/40"
              >
                <i className="ri-vip-crown-line text-xl"></i>
                Choose My Plan &amp; Start Learning
                <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href="#about"
                className="px-8 py-4 border-2 border-white/30 text-white rounded-xl font-bold hover:bg-white/10 transition-all whitespace-nowrap"
              >
                Meet Mr. Augustine
              </a>
            </div>
            <p className="text-gray-500 text-xs mt-4">30-day money-back guarantee · Cancel anytime · Instant access</p>
          </div>
        </div>

      </div>
    </section>
  );
}
